import { MASTER_DOCUMENT_TRANSLATIONS_ES } from '../i18n';
import type { LineActionEntry } from '../template/MasterDocumentTemplate.types';
import type { DocumentLineData } from '../MasterDocument.types';
import type { IMasterDocumentAdapter, ActionResult, DocumentState } from '../adapter/MasterDocumentAdapter.types';

// ════════════════════════════════════════════════════════════
// LINE ACTION GATING — Visibilidad/habilitación de acciones de línea (D2)
// ════════════════════════════════════════════════════════════

/**
 * Evalúa el gateo de una acción de línea adicional (declarada en
 * `template.lineActionRegistry`) SIN asumir un `line.status` canónico. Lo usa
 * `getLineActions` en `SmartMasterDocument` para decidir, por fila, si la acción
 * es visible y si está deshabilitada:
 *
 * - `statusField` + `visibleStates`/`disabledStates` → contra `line[statusField]`.
 * - `visibleDocStates`/`disabledDocStates` → contra el `docState` del documento.
 * - `permissionVisibility` → oculta si el usuario no tiene el permiso.
 * - Sin declaración → visible y habilitada.
 */
export function evaluateLineActionGate(
  entry: LineActionEntry,
  line: DocumentLineData,
  docState: string,
  permissions: string[] | null,
): { visible: boolean; disabled: boolean } {
  if (
    entry.permissionVisibility &&
    permissions !== null &&
    !permissions.includes(entry.permissionVisibility)
  ) {
    return { visible: false, disabled: false };
  }

  const statusValue =
    entry.statusField != null ? String(line[entry.statusField] ?? '') : undefined;

  if (entry.visibleStates && (statusValue == null || !entry.visibleStates.includes(statusValue))) {
    return { visible: false, disabled: false };
  }
  if (entry.visibleDocStates && !entry.visibleDocStates.includes(docState)) {
    return { visible: false, disabled: false };
  }

  const disabledByState =
    statusValue != null && (entry.disabledStates?.includes(statusValue) ?? false);
  const disabledByDocState = entry.disabledDocStates?.includes(docState) ?? false;

  return { visible: true, disabled: disabledByState || disabledByDocState };
}

/**
 * Sustituye los placeholders del `endpoint.path` de una acción de línea por el
 * documentId y el lineId. Soporta `{id}` y `{documentId}` (documento) y
 * `{lineId}` (línea). Lo usa la vía POR DEFECTO de ejecución de acciones de línea
 * (`adapter.genericFetch`) cuando el adapter no implementa `executeLineAction`.
 */
export function buildLineActionPath(
  path: string,
  documentId: string | undefined,
  lineId: string,
): string {
  return path
    .replace(/\{id\}/g, documentId ?? '')
    .replace(/\{documentId\}/g, documentId ?? '')
    .replace(/\{lineId\}/g, lineId);
}

/**
 * Resultado de ejecutar una acción de línea: separa la DECISIÓN (qué pasó y cómo
 * resincronizar) de los efectos (dispatch/toast), para poder testearla sin render.
 */
export interface LineActionOutcome {
  /** `false` ⇒ mostrar `error` en el modal y no cerrar. */
  ok: boolean;
  /** Mensaje de éxito (toast) cuando `ok`. */
  message?: string;
  /** Mensaje de error (modal) cuando `!ok`. */
  error?: string;
  /**
   * Clave i18n del error cuando el mensaje lo generó el kit y no el backend.
   *
   * Este módulo es una función pura consumida desde hooks, así que no puede llamar
   * a `useMasterDocumentTranslation`. En su lugar propaga la clave: el hook
   * llamante hace `errorKey ? t(errorKey) : error`. `error` viene relleno con el
   * texto español del catálogo para que un consumidor que ignore `errorKey` siga
   * mostrando algo legible.
   */
  errorKey?: string;
  /** Si presente, el caller hace `MERGE_STATE` con este payload. */
  merge?: Partial<DocumentState>;
  /** Si `true`, el caller recarga el documento (`getDocument` + `LOAD_SUCCESS`). */
  refetch?: boolean;
}

/**
 * Ejecuta una acción de línea adicional y decide cómo resincronizar el estado.
 * Dos vías:
 *
 *  - OVERRIDE (`adapter.executeLineAction`): lógica bespoke; el sync depende solo
 *    de su `updatedData` (el override es responsable de devolver lo que cambió).
 *    NO se fuerza refetch.
 *  - GENÉRICA (`endpoint` + `adapter.genericFetch`): ejecuta el endpoint
 *    interpolado. Como el kit no puede inferir `lineStatuses`/`docState`, si la
 *    respuesta NO trae `updatedData` (típico 204) marca `refetch` para que la UI
 *    no quede stale.
 */
/** Clave i18n del error genérico cuando el backend no envía mensaje propio. */
const ACTION_FAILED_KEY = 'master_document.error.actionFailed';

/**
 * Normaliza el fallo de una acción de línea.
 *
 * Si el backend mandó `message`, se respeta tal cual (ya llega traducido: el kit
 * envía `Accept-Language`). Si no, se propaga la clave del kit junto con su texto
 * español, para que el hook llamante pueda traducirla al idioma activo.
 */
function actionFailed(message?: string): LineActionOutcome {
  if (message) return { ok: false, error: message };
  return {
    ok: false,
    error: MASTER_DOCUMENT_TRANSLATIONS_ES[ACTION_FAILED_KEY],
    errorKey: ACTION_FAILED_KEY,
  };
}

export async function runLineAction(
  adapter: IMasterDocumentAdapter,
  state: DocumentState,
  entry: LineActionEntry,
  actionKey: string,
  lineId: string,
  payload: Record<string, unknown>,
): Promise<LineActionOutcome> {
  if (adapter.executeLineAction) {
    const result = await adapter.executeLineAction(actionKey, lineId, payload, state);
    if (!result.success) return actionFailed(result.message);
    // Override: el sync depende exclusivamente de su `updatedData`.
    return { ok: true, message: result.message, merge: result.updatedData };
  }

  if (entry.endpoint && adapter.genericFetch) {
    const path = buildLineActionPath(entry.endpoint.path, state.documentId, lineId);
    const res = await adapter.genericFetch(entry.endpoint.method, path, payload);
    const result: ActionResult = res && typeof res === 'object' && 'success' in (res as object)
      ? (res as ActionResult)
      : { success: true };
    if (!result.success) return actionFailed(result.message);
    if (result.updatedData) return { ok: true, message: result.message, merge: result.updatedData };
    // Sin datos frescos en la respuesta → recargar el documento.
    return { ok: true, message: result.message, refetch: !!state.documentId && !!adapter.getDocument };
  }

  return {
    ok: false,
    error: 'No hay forma de ejecutar la acción de línea: implementa adapter.executeLineAction o adapter.genericFetch.',
  };
}
